import './ExportFeedback.scss';

import * as React from 'react';

import { translate } from '~l10n/client';

import { IconButton, UIButton } from '~shared/components/buttons';
import { DialogTitle } from '~shared/components/Dialog';
import { useDialogClose } from '~shared/components/Dialog';
import { IconArrow } from '~shared/components/icons/large';
import { Stack } from '~shared/components/Stack';

import { Error } from './ExportFeedbackIcons';

interface ActionButtonProps {
  onClick: () => void;
  label: React.ReactNode;
  icon?: React.ReactElement;
}

interface Props {
  onBack?: () => void;
  icon?: React.ReactElement;
  title?: React.ReactNode;
  message?: React.ReactNode;
  actionButtonProps?: ActionButtonProps;
}

const ExportFeedback: React.FC<Props> = ({
  onBack,
  icon,
  title,
  message,
  actionButtonProps,
}: Props) => {
  const closeButtonAction = useDialogClose();

  // Default to close button
  const buttonProps = actionButtonProps || {
    label: translate('Close'),
    onClick: closeButtonAction,
    icon: null,
  };

  return (
    <>
      {onBack && (
        <div className="absolute top-0 left-0 p-6">
          <IconButton appearance="ghost" onClick={() => onBack()}>
            <IconArrow direction="left" />
          </IconButton>
        </div>
      )}
      <div className="flex items-center justify-center w-full h-full p-4 sm:p-8">
        <div className="text-center">
          <Stack space="8">
            {icon ? icon : null}
            <Stack space="4">
              <h3 className="m-0">
                <DialogTitle>{title}</DialogTitle>
              </h3>
              {message ? (
                <p className="m-0 font-sans text-base leading-8 text-blue-900">
                  {message}
                </p>
              ) : null}
            </Stack>
            <UIButton
              className="min-w-60"
              onClick={() => buttonProps.onClick()}
            >
              <div className="flex items-center justify-center gap-2">
                {buttonProps.icon}
                {buttonProps.label}
              </div>
            </UIButton>
          </Stack>
        </div>
      </div>
    </>
  );
};

interface GenericError {
  message: string;
  detail: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isGenericError = (o: any | GenericError): o is GenericError => {
  return (
    (o as GenericError).detail !== undefined &&
    (o as GenericError).message !== undefined
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatDetail = (o: any | GenericError) =>
  isGenericError(o) ? (
    <>
      <strong>{o.message}:</strong> <span>{o.detail}</span>
    </>
  ) : null;

interface DefaultErrorProps extends Pick<Props, 'onBack'> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any | GenericError;
}

interface RetryErrorProps extends DefaultErrorProps {
  onRetry: () => void;
}

const ExportFeedbackDefaultError: React.FC<DefaultErrorProps> = ({
  onBack,
  error,
}: DefaultErrorProps) => {
  return (
    <ExportFeedback
      onBack={onBack}
      icon={<Error />}
      title={translate('Something Went Wrong')}
      message={
        <>
          {translate('Please try again or contact support for assistance.')}
          <br />
          {formatDetail(error)}
        </>
      }
    />
  );
};

const ExportFeedbackRetryError: React.FC<RetryErrorProps> = ({
  onBack,
  error,
  onRetry,
}: RetryErrorProps) => {
  return (
    <ExportFeedback
      onBack={onBack}
      icon={<Error />}
      title={translate('Export Failed')}
      message={
        <>
          {translate('Something went wrong while exporting your results.')}
          <br />
          {formatDetail(error)}
        </>
      }
      actionButtonProps={{
        label: translate('Try Again'),
        onClick: onRetry,
      }}
    />
  );
};

export { ExportFeedback, ExportFeedbackDefaultError, ExportFeedbackRetryError };
